import { type ReactNode, useMemo } from 'react';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import { Box, Chip, Skeleton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { useRequirementCatalog } from '../store/hooks';
import { useRequirementMissingCount } from '../store/useRequirementMissingCount';
import type { DefinitionResponse, SubjectLanguage } from '../types';

export interface RequirementRailRowProps {
  /** The practice's own name for the requirement, not the kind. */
  label: string;
  /** Patients whose requirement is still open. Undefined until the count lands. */
  count?: number;
  /**
   * Activation is still evaluating the panel (AC-21), so the rows this count
   * would read do not all exist yet.
   */
  isChecking: boolean;
  isCountLoading: boolean;
  isCountError: boolean;
  subject: SubjectLanguage;
  /** True while the patient list is filtered to this requirement. */
  isActive?: boolean;
  /**
   * Absent when the row cannot filter — the parameter is not available, or a
   * search is active and the two cannot combine. The row then renders as a
   * plain row rather than a disabled button: there is nothing to press, so
   * nothing offers to be pressed.
   */
  onSelect?: () => void;
}

/**
 * One requirement in the rail: the practice's label, and how many patients
 * still owe it.
 *
 * Not a button. The click that filters the list needs GDN-3423's
 * `patient_requirement_definition_id` parameter, which is not in the contract
 * yet; a row that looked clickable and filtered nothing would be worse than a
 * row that plainly does not.
 */
export const RequirementRailRow = ({
  label,
  count,
  isChecking,
  isCountLoading,
  isCountError,
  subject,
  isActive = false,
  onSelect,
}: RequirementRailRowProps) => {
  const theme = useTheme();

  const interactive = Boolean(onSelect);

  return (
    <Box
      component="li"
      onClick={onSelect}
      onKeyDown={
        interactive
          ? event => {
              // `event.repeat` guards a held key: keydown fires continuously,
              // and each one would toggle the filter and emit its analytics
              // event again.
              if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? isActive : undefined}
      aria-label={
        interactive
          ? `Filter by ${label}${
              count === undefined
                ? ''
                : `, ${count} ${count === 1 ? subject.singular : subject.plural}`
            }`
          : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        cursor: interactive ? 'pointer' : 'default',
        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
        borderLeft: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        '&:hover': interactive
          ? {
              bgcolor: isActive
                ? alpha(theme.palette.primary.main, 0.12)
                : theme.palette.action.hover,
            }
          : undefined,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: theme.palette.text.primary,
        }}
        title={label}
      >
        {label}
      </Typography>

      {isChecking ? (
        // Never a number here, and never a zero: until activation finishes,
        // "0" would read as "nobody is missing this" when the truth is "we
        // have not finished looking".
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Checking…
        </Typography>
      ) : isCountError ? (
        <Tooltip title="Count unavailable — try reloading" arrow>
          <Typography
            variant="caption"
            // A bare em dash is read as nothing at all, so the label carries
            // the meaning the glyph has only visually.
            aria-label="count unavailable"
            sx={{ color: theme.palette.text.disabled }}
          >
            —
          </Typography>
        </Tooltip>
      ) : isCountLoading || count === undefined ? (
        <Skeleton width={24} height={16} />
      ) : (
        <Chip
          label={count.toLocaleString()}
          size="small"
          // The row already names the requirement, so this says what the bare
          // number means without making a screen reader hear the label twice.
          aria-label={`${count.toLocaleString()} ${
            count === 1 ? subject.singular : subject.plural
          } missing`}
          sx={{
            height: 20,
            minWidth: 28,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
    </Box>
  );
};

export interface RequirementsRailCardShellProps {
  subject: SubjectLanguage;
  children: ReactNode;
}

/**
 * The card around the rows. Presentational, so a story can render every row
 * state at once without a store.
 */
export const RequirementsRailCardShell = ({
  subject,
  children,
}: RequirementsRailCardShellProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.grey[100]}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          Requirements
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: theme.palette.text.secondary }}
        >
          {subject.plural} missing an item
        </Typography>
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, px: 0, py: 0.5 }}>
        {children}
      </Box>
    </Box>
  );
};

/**
 * One row's count. Its own component because the endpoint takes one definition
 * id and hooks cannot be called in a loop — N definitions means N subscriptions,
 * and each row owns its own loading and error state rather than one failure
 * blanking the card.
 */
const ConnectedRequirementRailRow = ({
  definition,
  subject,
  isActive,
  onSelect,
}: {
  definition: DefinitionResponse;
  subject: SubjectLanguage;
  isActive: boolean;
  onSelect?: (count?: number) => void;
}) => {
  const isChecking = definition.materialization_status === 'checking';

  const { count, isLoading, isError } = useRequirementMissingCount(
    definition.patient_requirement_definition_id,
    // Asking while activation still runs would return a partial count that
    // looks final. The catalog poll below flips this to done and the count
    // starts then.
    { skip: isChecking },
  );

  return (
    <RequirementRailRow
      label={definition.label}
      count={count}
      isChecking={isChecking}
      isCountLoading={isLoading}
      isCountError={isError}
      subject={subject}
      isActive={isActive}
      // A definition still materializing has no settled population to filter
      // to, so the row informs and does not offer. Withheld rather than
      // disabled, for the same reason as everywhere else on this surface.
      onSelect={onSelect && !isChecking ? () => onSelect(count) : undefined}
    />
  );
};

/**
 * The patients-list rail card (AC-23): one row per active requirement
 * definition with the count of patients missing it.
 *
 * The card launches nothing — bulk send lives on the Settings catalog row
 * (AC-10) — and it filters nothing yet (see `RequirementRailRow`).
 *
 * Gated twice, deliberately. The Statsig gate is rollout: a gate-off org sees
 * no requirements anywhere. `manage_billing` is a WORKFLOW gate, not a security
 * boundary — the endpoints behind this card are open to any provider, and the
 * PM ruling on GDN-3423 says they stay that way. It is here because chasing
 * requirements is billing's job, and the filtered list this card leads to is
 * AC-2's missing list, which carries the same check (PM ruling on GDN-3422).
 * Withheld rather than shown-and-refused: staff without it see no card.
 */
export interface RequirementsRailCardProps {
  /** The definition the list is currently filtered to, if any. */
  activeDefinitionId?: string | null;
  /**
   * Every definition the list is filtered to, when it takes several
   * (ADR-012). Takes precedence over `activeDefinitionId`.
   */
  activeDefinitionIds?: string[];
  /**
   * Removes one definition when its active row is clicked. Without it, an
   * active row clears the whole filter through `onClear`.
   */
  onDeselect?: (definition: { id: string; kind: string }) => void;
  /**
   * Render below the rail's breakpoint too, for a phone menu the user opened.
   * Counts are only spent when someone can see them.
   */
  showOnSmallScreens?: boolean;
  /**
   * Absent when the rail cannot filter at all. Present but ignored is not an
   * option: the rows read as buttons only when this is here.
   */
  onSelect?: (definition: { id: string; label: string; kind: string; count?: number }) => void;
  onClear?: () => void;
  /**
   * The rail's own disabled state — a search is active, and a search and a
   * requirement filter cannot both apply. The rows stop offering the click
   * rather than offering one the list will not honour.
   */
  disabled?: boolean;
}

export const RequirementsRailCard = ({
  activeDefinitionId,
  activeDefinitionIds,
  onDeselect,
  showOnSmallScreens = false,
  onSelect,
  onClear,
  disabled = false,
}: RequirementsRailCardProps = {}) => {
  const theme = useTheme();
  const enabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const canManageBilling = useCan('manage_billing');
  const subject = useSubjectLanguageVariants();

  // The rail is desktop-only, and it hides itself with `display: none` rather
  // than unmounting — so without this the card would still read the catalog and
  // spend one count request per definition on every phone visit to the patients
  // list, for pixels nobody can see. `up('md')` is the same breakpoint the rail
  // hides at; the two have to move together.
  const railIsVisible = useMediaQuery(theme.breakpoints.up('md'));

  const visible = enabled && canManageBilling && (railIsVisible || showOnSmallScreens);

  const { definitions, isLoading } = useRequirementCatalog({
    skip: !visible,
    pollWhileChecking: visible,
  });

  const activeDefinitions = useMemo(
    () =>
      definitions
        .filter(definition => definition.active)
        .sort((a, b) => a.sort_order - b.sort_order),
    [definitions],
  );

  if (!visible) return null;

  // Nothing while the catalog loads, rather than a skeleton card: we do not
  // yet know whether there IS a card, and a placeholder that then disappears
  // moves the cards below it out from under the pointer.
  if (isLoading) return null;

  // An org with no active definitions gets no card. A catalog that failed to
  // load lands here too, and that is deliberate: the rail is not the place to
  // report an org-config read failure, and the patients list still works.
  if (activeDefinitions.length === 0) return null;

  return (
    <RequirementsRailCardShell subject={subject}>
      {activeDefinitions.map(definition => {
        const isActive = activeDefinitionIds
          ? activeDefinitionIds.includes(definition.patient_requirement_definition_id)
          : activeDefinitionId === definition.patient_requirement_definition_id;

        return (
          <ConnectedRequirementRailRow
            key={definition.patient_requirement_definition_id}
            definition={definition}
            subject={subject}
            isActive={isActive}
            onSelect={
              onSelect && !disabled
                ? count =>
                    // Clicking the active row clears, exactly as the payer card
                    // behaves — the rail is one control, not two.
                    isActive
                      ? onDeselect
                        ? onDeselect({
                            id: definition.patient_requirement_definition_id,
                            kind: definition.kind,
                          })
                        : onClear?.()
                      : onSelect({
                          id: definition.patient_requirement_definition_id,
                          label: definition.label,
                          kind: definition.kind,
                          count,
                        })
                : undefined
            }
          />
        );
      })}
    </RequirementsRailCardShell>
  );
};
